import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export interface Property {
  id: string;
  title: string;
  location: string;
  price: number;
  price_per_sqm: number;
  bedrooms: number;
  bathrooms: number;
  area: number;
  description?: string;
  type: 'Apartment' | 'House' | 'Land' | 'Commercial';
  listing_type: 'Prodaja' | 'Najam';
  features: string[];
  image?: string;
  images?: string[];
  is_under_market: boolean;
  // Polja za najam
  rental_price?: number;
  agency_commission_amount?: number; // Promijenio s rental_commission
  rental_duration?: string;
  deposit?: number;
  availability_date?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Lead {
  id?: string;
  name: string;
  email?: string;
  phone?: string;
  message?: string;
  source: string;
  property_id?: string; // Nova polja za povezivanje s nekretninom
  is_read?: boolean; // Nova polja za označavanje pročitanih
  status?: string;
  created_at?: string;
  updated_at?: string;
}

// Property Service
export const propertyService = {
  async getAll() {
    const { data, error } = await supabase
      .from('properties')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('properties')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  async create(property: Omit<Property, 'id' | 'created_at'>) {
    const { data, error } = await supabase
      .from('properties')
      .insert([property])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, property: Partial<Property>) {
    const { data, error } = await supabase
      .from('properties')
      .update(property)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('properties')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Upload slike u Supabase Storage
  async uploadImage(propertyId: string, file: File): Promise<string> {
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    const fileName = `${propertyId}/${Date.now()}.${fileExt}`;
    
    const { data, error } = await supabase.storage
      .from('property-images')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) throw error;
    
    // Dohvati javni URL
    const { data: { publicUrl } } = supabase.storage
      .from('property-images')
      .getPublicUrl(data.path);
    
    return publicUrl;
  },

  // Obriši sliku iz Storage-a
  async deleteImage(imageUrl: string) {
    // Izvuci path iz URL-a
    const path = imageUrl.split('/property-images/')[1];
    
    const { error } = await supabase.storage
      .from('property-images')
      .remove([path]);
    
    if (error) throw error;
  },

  // Ažuriraj images array za property
  async updateImages(propertyId: string, images: string[], imageAlts?: string[]) {
    const { data, error } = await supabase
      .from('properties')
      .update({ 
        images,
        image_alts: imageAlts || images.map(() => '')
      })
      .eq('id', propertyId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
};

export const leadService = {
  async getAll(): Promise<Lead[]> {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getUnreadCount(): Promise<number> {
    const { count, error } = await supabase
      .from('leads')
      .select('*', { count: 'exact', head: true })
      .eq('is_read', false);
    
    if (error) throw error;
    return count || 0;
  },

  async create(lead: Omit<Lead, 'id' | 'created_at' | 'updated_at'>): Promise<Lead> {
    const { data, error } = await supabase
      .from('leads')
      .insert([{
        ...lead,
        status: lead.status || 'novo',
        is_read: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async markAsRead(id: string): Promise<Lead> {
    const { data, error } = await supabase
      .from('leads')
      .update({ 
        is_read: true,
        updated_at: new Date().toISOString() 
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateStatus(id: string, status: string): Promise<Lead> {
    const { data, error } = await supabase
      .from('leads')
      .update({ 
        status,
        updated_at: new Date().toISOString() 
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('leads')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};


